/**
 * PRP-MCP-5 G9: state for the Original | Enhanced switch on an imported estimate.
 *
 * Enhanced (the current estimate) is the default. Original is the import as it
 * arrived, fetched from GET /imports/{hash}/original the first time it is chosen.
 * Switching only changes what is shown; nothing is saved and export is unchanged.
 *
 * The switch is offered only when the server says an original is stored and
 * differs from the current estimate (`has_original`), and never on a
 * chat-created estimate (id starts with `mcp-`).
 */
import { useEffect, useState } from 'react';

import { BackendAPI } from '../../lib/api';
import type { ParsedEstimateResponse } from '../../types';

export type EstimateVersion = 'original' | 'enhanced';

/** Mirrors the backend's MCP_ID_PREFIX (app/mcp/estimates.py). */
const CHAT_PREFIX = 'mcp-';

/** True when the Original | Enhanced switch should show for this estimate. */
export function canShowOriginal(result: ParsedEstimateResponse | null | undefined): boolean {
  if (!result?.parse_hash || result.parse_hash.startsWith(CHAT_PREFIX)) return false;
  // GET /imports/{hash} sets has_original: a stored original exists and differs.
  return (result as { has_original?: boolean }).has_original === true;
}

export interface OriginalView {
  visible: boolean;
  version: EstimateVersion;
  setVersion: (_v: EstimateVersion) => void;
  /** The original, once loaded (null until then, or when it could not be read). */
  original: ParsedEstimateResponse | null;
  loading: boolean;
  error: string | null;
}

/**
 * @param result - The estimate on screen.
 * @param resetKey - Any change (e.g. the page's view mode) returns the switch to Enhanced.
 */
export function useOriginalView(
  result: ParsedEstimateResponse | null | undefined,
  resetKey?: unknown,
): OriginalView {
  const visible = canShowOriginal(result);
  const hash = result?.parse_hash ?? null;
  const [version, setVersion] = useState<EstimateVersion>('enhanced');
  const [original, setOriginal] = useState<ParsedEstimateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A different estimate (or view): back to Enhanced, forget the old original.
  useEffect(() => {
    setVersion('enhanced');
  }, [hash, resetKey]);
  useEffect(() => {
    setOriginal(null);
    setError(null);
  }, [hash]);

  useEffect(() => {
    if (version !== 'original' || !visible || !hash || original) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    BackendAPI.getImportOriginal(hash)
      .then((res) => {
        if (!cancelled) setOriginal(res);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the original import.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [version, visible, hash, original]);

  return {
    visible,
    version: visible ? version : 'enhanced',
    setVersion,
    original,
    loading,
    error,
  };
}
